import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const LoanAnalysis = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCapitalModal, setShowCapitalModal] = useState(false);
  const [newCapital, setNewCapital] = useState('200000');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${config.API_URL}/enhanced-loans/dashboard/analytics`);
      console.log('Analytics response:', response.data);
      if (response.data.success) {
        setAnalytics(response.data);
      } else {
        setError('Failed to load analytics data');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const updateCapital = async () => {
    try {
      const response = await axios.post(`${config.API_URL}/enhanced-loans/dashboard/set-capital`, {
        initialCapital: parseFloat(newCapital)
      });
      
      if (response.data.success) {
        alert('Capital updated successfully!');
        setShowCapitalModal(false);
        fetchAnalytics(); // Refresh data
      }
    } catch (err) {
      console.error('Error updating capital:', err);
      alert('Failed to update capital');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'info';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading Comprehensive Analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || 'Failed to load data'}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchAnalytics}>
            <i className="bi bi-arrow-clockwise me-1"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  const { businessSettings, cashFlow, loanStats, npa, portfolioHealth, monthlyData, projectedReturns, creditAnalytics } = analytics;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-graph-up-arrow text-primary me-2"></i>
            Loan Business Dashboard
          </h2>
          <p className="text-muted mb-0">Cash Flow & Growth Tracking | Since {formatDate(businessSettings.businessStartDate)}</p>
        </div>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={() => setShowCapitalModal(true)}>
            <i className="bi bi-gear me-1"></i> Set Capital
          </button>
          <button className="btn btn-primary" onClick={fetchAnalytics}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>
      </div>

      {/* Capital Overview - Hero Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-lg border-0" style={{background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
            <div className="card-body p-4">
              <div className="row align-items-center">
                <div className="col-md-3 text-center border-end border-white border-opacity-25">
                  <h6 className="text-white text-opacity-75 mb-2 fw-normal" style={{fontSize: '0.9rem'}}>Initial Capital</h6>
                  <h2 className="mb-0 fw-bold text-white" style={{fontSize: '2rem'}}>{formatCurrency(businessSettings.initialCapital)}</h2>
                  <small className="text-white text-opacity-75">Starting Investment</small>
                </div>
                <div className="col-md-3 text-center border-end border-white border-opacity-25">
                  <h6 className="text-white text-opacity-75 mb-2 fw-normal" style={{fontSize: '0.9rem'}}>Available Cash</h6>
                  <h2 className="mb-0 fw-bold text-warning" style={{fontSize: '2rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)'}}>{formatCurrency(cashFlow.availableCash)}</h2>
                  <small className="text-white text-opacity-75">Ready to Deploy</small>
            </div>
                <div className="col-md-3 text-center border-end border-white border-opacity-25">
                  <h6 className="text-white text-opacity-75 mb-2 fw-normal" style={{fontSize: '0.9rem'}}>Cash Deployed</h6>
                  <h2 className="mb-0 fw-bold text-white" style={{fontSize: '2rem'}}>{formatCurrency(cashFlow.cashDeployed)}</h2>
                  <small className="text-white text-opacity-75">{cashFlow.capitalUtilization}% Utilized</small>
                </div>
                <div className="col-md-3 text-center">
                  <h6 className="text-white text-opacity-75 mb-2 fw-normal" style={{fontSize: '0.9rem'}}>Total Profit</h6>
                  <h2 className="mb-0 fw-bold text-success" style={{fontSize: '2rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)'}}>{formatCurrency(loanStats.totalProfit)}</h2>
                  <small className="text-white text-opacity-75">ROI: {cashFlow.roi}%</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Received</h6>
                  <h4 className="text-success mb-0">{formatCurrency(cashFlow.totalReceived)}</h4>
                  <small className="text-muted">Collections</small>
                </div>
                <div className="text-success" style={{fontSize: '2.5rem'}}>
                  <i className="bi bi-arrow-down-circle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-danger">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Pending Recovery</h6>
                  <h4 className="text-danger mb-0">{formatCurrency(cashFlow.totalOutstanding)}</h4>
                  <small className="text-muted">{loanStats.activeLoans} active loans</small>
                </div>
                <div className="text-danger" style={{fontSize: '2.5rem'}}>
                  <i className="bi bi-hourglass-split"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">NPA Amount</h6>
                  <h4 className="text-warning mb-0">{formatCurrency(npa.npaAmount)}</h4>
                  <small className="text-muted">{npa.npaLoans} loans (30+ days)</small>
                </div>
                <div className="text-warning" style={{fontSize: '2.5rem'}}>
                  <i className="bi bi-exclamation-triangle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card border-${getHealthColor(portfolioHealth.score)}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Portfolio Health</h6>
                  <h4 className={`text-${getHealthColor(portfolioHealth.score)} mb-0`}>{portfolioHealth.score}/100</h4>
                  <small className="text-muted">Health Score</small>
                </div>
                <div className={`text-${getHealthColor(portfolioHealth.score)}`} style={{fontSize: '2.5rem'}}>
                  <i className="bi bi-shield-check"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Cash Flow Trend */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2"></i>
                Monthly Cash Flow (Last 12 Months)
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="text-end">Disbursed</th>
                      <th className="text-end">Received</th>
                      <th className="text-end">Net Cash Flow</th>
                      <th className="text-center">Loans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((month, index) => (
                      <tr key={index}>
                        <td><strong>{month.month}</strong></td>
                        <td className="text-end text-danger">{formatCurrency(month.disbursed)}</td>
                        <td className="text-end text-success">{formatCurrency(month.received)}</td>
                        <td className={`text-end fw-bold ${month.netCashFlow >= 0 ? 'text-success' : 'text-danger'}`}>
                          {month.netCashFlow >= 0 ? '+' : ''}{formatCurrency(month.netCashFlow)}
                        </td>
                        <td className="text-center">
                          <span className="badge bg-info">{month.loanCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr>
                      <th>Total</th>
                      <th className="text-end">{formatCurrency(monthlyData.reduce((sum, m) => sum + m.disbursed, 0))}</th>
                      <th className="text-end">{formatCurrency(monthlyData.reduce((sum, m) => sum + m.received, 0))}</th>
                      <th className={`text-end fw-bold ${monthlyData.reduce((sum, m) => sum + m.netCashFlow, 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(monthlyData.reduce((sum, m) => sum + m.netCashFlow, 0))}
                      </th>
                      <th className="text-center">{monthlyData.reduce((sum, m) => sum + m.loanCount, 0)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Projected Returns */}
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">
                <i className="bi bi-calendar-check me-2"></i>
                Projected Returns (Next 6 Months)
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="text-end">Expected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectedReturns.map((proj, index) => (
                      <tr key={index}>
                        <td><strong>{proj.month}</strong></td>
                        <td className="text-end text-success fw-bold">{formatCurrency(proj.expectedCollection)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr>
                      <th>Total Expected</th>
                      <th className="text-end text-success">
                        {formatCurrency(projectedReturns.reduce((sum, p) => sum + p.expectedCollection, 0))}
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loan Stats & Risk Analysis */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-secondary text-white">
              <h5 className="mb-0"><i className="bi bi-list-check me-2"></i>Loan Portfolio Overview</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Total Loans:</span>
                    <strong>{loanStats.totalLoans}</strong>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-success">Active:</span>
                    <strong className="text-success">{loanStats.activeLoans}</strong>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-info">Completed:</span>
                    <strong className="text-info">{loanStats.completedLoans}</strong>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-warning">Pending:</span>
                    <strong className="text-warning">{loanStats.pendingLoans}</strong>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-danger">Rejected:</span>
                    <strong className="text-danger">{loanStats.rejectedLoans}</strong>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-dark">Defaulted:</span>
                    <strong className="text-dark">{loanStats.defaultedLoans}</strong>
                  </div>
                </div>
              </div>
              <hr />
              <div className="row">
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between">
                    <span>Total Requested:</span>
                    <strong>{formatCurrency(loanStats.totalRequested)}</strong>
                  </div>
                </div>
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between">
                    <span>Total Disbursed:</span>
                    <strong>{formatCurrency(loanStats.totalDisbursed)}</strong>
                  </div>
                </div>
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-warning fw-semibold">Processing Fees:</span>
                    <strong className="text-warning fs-5">{formatCurrency(loanStats.totalProcessingFees)}</strong>
                  </div>
                </div>
                {loanStats.totalLateFees > 0 && (
                  <div className="col-12 mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-danger fw-semibold">Late Fees Collected:</span>
                      <strong className="text-danger fs-5">{formatCurrency(loanStats.totalLateFees)}</strong>
                    </div>
                  </div>
                )}
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-info fw-semibold">Interest Earned:</span>
                    <strong className="text-info fs-5">{formatCurrency(loanStats.totalInterestEarned)}</strong>
                  </div>
                </div>
                <div className="col-12 pt-2 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-success fw-bold fs-6">Total Profit:</span>
                    <strong className="text-success fs-4">{formatCurrency(loanStats.totalProfit)}</strong>
                  </div>
                  <small className="text-muted">Interest + Processing Fees + Late Fees</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0"><i className="bi bi-exclamation-triangle me-2"></i>Risk Distribution</h5>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-dark">Very High Risk</span>
                  <strong className="text-dark">{loanStats.riskDistribution.very_high}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-dark"
                    style={{ width: `${(loanStats.riskDistribution.very_high / loanStats.totalLoans) * 100}%` }}
                  >
                    {((loanStats.riskDistribution.very_high / loanStats.totalLoans) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span className="text-danger">High Risk</span>
                  <strong className="text-danger">{loanStats.riskDistribution.high}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-danger"
                    style={{ width: `${(loanStats.riskDistribution.high / loanStats.totalLoans) * 100}%` }}
                  >
                    {((loanStats.riskDistribution.high / loanStats.totalLoans) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span className="text-warning">Medium Risk</span>
                  <strong className="text-warning">{loanStats.riskDistribution.medium}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-warning"
                    style={{ width: `${(loanStats.riskDistribution.medium / loanStats.totalLoans) * 100}%` }}
                  >
                    {((loanStats.riskDistribution.medium / loanStats.totalLoans) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span className="text-success">Low Risk</span>
                  <strong className="text-success">{loanStats.riskDistribution.low}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${(loanStats.riskDistribution.low / loanStats.totalLoans) * 100}%` }}
                  >
                    {((loanStats.riskDistribution.low / loanStats.totalLoans) * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NPA & Credit Score */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card border-warning">
            <div className="card-header bg-warning text-dark">
              <h5 className="mb-0"><i className="bi bi-exclamation-octagon me-2"></i>NPA (Non-Performing Assets)</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6">
                  <h6 className="text-muted">NPA Loans</h6>
                  <h3 className="text-warning mb-0">{npa.npaLoans}</h3>
                </div>
                <div className="col-6">
                  <h6 className="text-muted">NPA Amount</h6>
                  <h3 className="text-warning mb-0">{formatCurrency(npa.npaAmount)}</h3>
                </div>
              </div>
              <div className="alert alert-warning mb-3">
                <strong>NPA Rate: {npa.npaRate}%</strong>
                <div className="progress mt-2" style={{height: '10px'}}>
                  <div className="progress-bar bg-warning" style={{width: `${npa.npaRate}%`}}></div>
                </div>
              </div>
              {npa.details && npa.details.length > 0 ? (
                <>
                  <h6 className="text-muted mb-2">Top NPA Loans:</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th className="text-end">Amount</th>
                          <th className="text-end">Days Overdue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {npa.details.map((loan, index) => (
                          <tr key={index}>
                            <td>{loan.customerName}</td>
                            <td className="text-end">{formatCurrency(loan.amount)}</td>
                            <td className="text-end">
                              <span className="badge bg-danger">{loan.overdueDays} days</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  No NPA loans! All payments are on track.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0"><i className="bi bi-graph-up me-2"></i>Credit Score Distribution</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Excellent (700+) - 3% Rate</span>
                  <strong className="text-success">{creditAnalytics.creditDistribution.excellent}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${(creditAnalytics.creditDistribution.excellent / creditAnalytics.totalCustomersWithCreditScore) * 100}%` }}
                  >
                    {((creditAnalytics.creditDistribution.excellent / creditAnalytics.totalCustomersWithCreditScore) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span>Good (500-699) - 4% Rate</span>
                  <strong className="text-info">{creditAnalytics.creditDistribution.good}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-info"
                    style={{ width: `${(creditAnalytics.creditDistribution.good / creditAnalytics.totalCustomersWithCreditScore) * 100}%` }}
                  >
                    {((creditAnalytics.creditDistribution.good / creditAnalytics.totalCustomersWithCreditScore) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span>Poor (&lt;500) - 5% Rate</span>
                  <strong className="text-warning">{creditAnalytics.creditDistribution.poor}</strong>
                </div>
                <div className="progress mb-3" style={{height: '25px'}}>
                  <div
                    className="progress-bar bg-warning"
                    style={{ width: `${(creditAnalytics.creditDistribution.poor / creditAnalytics.totalCustomersWithCreditScore) * 100}%` }}
                  >
                    {((creditAnalytics.creditDistribution.poor / creditAnalytics.totalCustomersWithCreditScore) * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
              <hr />
              <div className="text-center">
                <h6 className="text-muted">Average Credit Score</h6>
                <h3 className="mb-0">{creditAnalytics.averageCreditScore}</h3>
                <small className="text-muted">Across {creditAnalytics.totalCustomersWithCreditScore} customers</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Performance Summary */}
          <div className="card">
        <div className="card-header bg-dark text-white">
          <h5 className="mb-0"><i className="bi bi-trophy me-2"></i>Business Performance Summary</h5>
            </div>
            <div className="card-body">
              <div className="row">
            <div className="col-md-4">
              <h6 className="text-muted mb-3">Capital Management</h6>
              <div className="mb-2">
                  <div className="d-flex justify-content-between">
                  <span>Initial Capital:</span>
                  <strong>{formatCurrency(businessSettings.initialCapital)}</strong>
                </div>
              </div>
              <div className="mb-2">
                  <div className="d-flex justify-content-between">
                  <span>Cash Deployed:</span>
                  <strong className="text-danger">{formatCurrency(cashFlow.cashDeployed)}</strong>
                </div>
                  </div>
              <div className="mb-2">
                  <div className="d-flex justify-content-between">
                  <span>Available Cash:</span>
                  <strong className="text-success">{formatCurrency(cashFlow.availableCash)}</strong>
                </div>
                  </div>
              <div className="mb-2">
                  <div className="d-flex justify-content-between">
                  <span className="fw-bold">Capital Utilization:</span>
                  <strong className="text-primary">{cashFlow.capitalUtilization}%</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <h6 className="text-muted mb-3 fw-bold">Profitability</h6>
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Processing Fees:</span>
                  <strong className="text-warning">{formatCurrency(loanStats.totalProcessingFees)}</strong>
                </div>
              </div>
              {loanStats.totalLateFees > 0 && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Late Fees:</span>
                    <strong className="text-danger">{formatCurrency(loanStats.totalLateFees)}</strong>
                  </div>
                </div>
              )}
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Interest Earned:</span>
                  <strong className="text-info">{formatCurrency(loanStats.totalInterestEarned)}</strong>
                </div>
                  </div>
              <div className="mb-2 pt-2 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Total Profit:</span>
                  <strong className="text-success fs-5">{formatCurrency(loanStats.totalProfit)}</strong>
                </div>
              </div>
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">ROI:</span>
                  <strong className="text-info fs-5">{cashFlow.roi}%</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <h6 className="text-muted mb-3">Portfolio Health</h6>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <span>Health Score:</span>
                  <strong className={`text-${getHealthColor(portfolioHealth.score)}`}>{portfolioHealth.score}/100</strong>
                </div>
                </div>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <span>Completion Rate:</span>
                  <strong>{portfolioHealth.completionRate}%</strong>
                </div>
              </div>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <span>Default Rate:</span>
                  <strong className="text-danger">{portfolioHealth.defaultRate}%</strong>
                </div>
                </div>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <span>NPA Rate:</span>
                  <strong className="text-warning">{npa.npaRate}%</strong>
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className={`alert alert-${getHealthColor(portfolioHealth.score)} mb-0`}>
            <i className="bi bi-lightbulb me-2"></i>
            <strong>Business Insight:</strong>
            {portfolioHealth.score >= 80 && ' Excellent portfolio health! Your business is performing very well with low risk.'}
            {portfolioHealth.score >= 60 && portfolioHealth.score < 80 && ' Good portfolio health. Consider reducing NPA and improving collection efficiency.'}
            {portfolioHealth.score >= 40 && portfolioHealth.score < 60 && ' Moderate portfolio health. Focus on reducing NPAs and improving loan quality.'}
            {portfolioHealth.score < 40 && ' Portfolio needs attention! High NPAs or defaults detected. Review loan approval criteria.'}
            {npa.npaLoans > 0 && ` You have ${npa.npaLoans} NPA loans worth ${formatCurrency(npa.npaAmount)}. Follow up with these customers urgently.`}
          </div>
        </div>
      </div>

      {/* Capital Settings Modal */}
      {showCapitalModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Set Initial Capital</h5>
                <button type="button" className="btn-close" onClick={() => setShowCapitalModal(false)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Initial Capital Amount (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={newCapital}
                  onChange={(e) => setNewCapital(e.target.value)}
                  placeholder="Enter amount (e.g., 200000)"
                />
                <small className="text-muted">
                  Current: {formatCurrency(businessSettings.initialCapital)}
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCapitalModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={updateCapital}>
                  Update Capital
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanAnalysis;
